import React, { useState } from 'react';
import { ArrowRight, Check, X, Play } from 'lucide-react';
import ApplicationModal from './ApplicationModel'; 

const LandingPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-black text-[#EDEDED] font-sans selection:bg-[#E50914] selection:text-white overflow-x-hidden relative">
      <ApplicationModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
      
      {/* GLOBAL FILM GRAIN OVERLAY - GIVES IT THAT 'CINEMA' TEXTURE */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.07] z-50 mix-blend-overlay" 
           style={{ backgroundImage: `url("https://grainy-gradients.vercel.app/noise.svg")` }}>
      </div>

      {/* NAVIGATION */}
      <nav className="fixed top-0 w-full px-6 py-6 md:px-12 flex justify-between items-center z-50 mix-blend-difference text-white">
        <img src="/logo192.png" alt="MotionX Logo" className="w-20 h-20" />
        <a 
          href="#apply"
          className="border border-white/30 px-6 py-2 rounded-full text-xs font-bold tracking-widest hover:bg-white hover:text-black transition-all duration-300 uppercase backdrop-blur-sm"
          onClick={() => setIsModalOpen(true)}
        >
          Apply Now
        </a>
      </nav>

      <main>
        

    {/* HERO SECTION WITH LOCAL VIDEO BACKGROUND */}
    <header className="h-screen flex flex-col justify-center px-6 md:px-12 relative border-b border-neutral-900 overflow-hidden">
      
      {/* LOCAL VIDEO BACKGROUND */}
      <div className="absolute inset-0 z-0">
        <video 
          autoPlay 
          loop 
          muted 
          playsInline 
          className="w-full h-full object-cover opacity-90 grayscale contrast-135"
        >
          {/* Ensure your file is named 'hero.mp4' and inside the 'public' folder */}
          <source src="/sr_motionx.mp4" type="video/mp4" />
        </video>
        
        {/* TEXTURE OVERLAYS */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/60"></div>
        <div className="absolute inset-0 bg-black/20 mix-blend-multiply"></div>
      </div>

      {/* CONTENT */}
      <div className="flex flex-col leading-[0.85] relative z-10 mix-blend-lighten">
    {/* FIXED: Added inline style for the outline effect */}
    <h1 
      className="font-display text-[14vw] md:text-[12vw] uppercase text-transparent opacity-10"
      style={{ WebkitTextStroke: '1px white' }}
    >
      CERTIFIED
    </h1>
    
    <h1 className="font-display text-[14vw] md:text-[12vw] uppercase text-white drop-shadow-2xl">
      AI FILMMAKER
    </h1>
  </div>
      
      <div className="mt-12 md:mt-16 flex flex-col md:flex-row justify-between items-start md:items-end gap-8 relative z-10">
        <p className="text-sm md:text-base font-mono text-neutral-300 max-w-md leading-relaxed uppercase tracking-wide drop-shadow-md">
          <span className="text-[#E50914] font-bold">STUDIO PRODUCTION INTENSIVE</span><br/>
          NOT A WORKSHOP. A CAREER SHIFT.<br/>
          BATCH 01 — HYDERABAD / REMOTE
        </p>
        
        <button className="group bg-[#E50914] text-white pl-8 pr-6 py-4 rounded-full font-bold uppercase tracking-widest text-sm hover:scale-105 transition-transform duration-300 flex items-center gap-4" onClick={() => setIsModalOpen(true)}>
          Apply for Batch 01 <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform"/>
        </button>
      </div>
    </header>

        {/* POSITIONING - IMAGE RIGHT */}
        <section className="py-32 px-6 md:px-12 border-b border-neutral-900 relative">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            
            <div className="relative z-10">
              <h2 className="font-display text-[7vw] leading-[0.9] uppercase mb-12">
                <span className="text-white block">We don't teach prompts.</span>
                <span className="text-neutral-700 block">We teach cinema.</span>
              </h2>
              <div className="space-y-8 max-w-lg">
                <p className="text-xl font-light leading-relaxed text-neutral-300">
                  MotionX is a production studio, not a school. We are opening our internal training protocols to find the next generation of filmmakers.
                </p>
                <div className="flex gap-4 items-center">
                  <span className="h-[1px] w-12 bg-[#E50914]"></span>
                  <span className="font-mono text-xs text-[#E50914] uppercase tracking-widest">No Job Guarantee. Just Real Work.</span>
                </div>
              </div>
            </div>

            {/* CINEMATIC INSERT IMAGE */}
            <div className="relative aspect-[4/5] md:aspect-[3/4]">
              <div className="absolute inset-0 bg-[#E50914] translate-x-4 translate-y-4"></div>
              <img 
                src="https://images.unsplash.com/photo-1536440136628-849c177e76a1?q=80&w=2525&auto=format&fit=crop" 
                alt="Filming Set" 
                className="relative w-full h-full object-cover grayscale contrast-125 brightness-75 hover:grayscale-0 transition-all duration-700"
              />
              <div className="absolute bottom-6 left-6 right-6">
                 <div className="bg-black/80 backdrop-blur-md p-4 border-l-2 border-[#E50914]">
                   <p className="font-mono text-[10px] uppercase text-neutral-400">Project: Alpha</p>
                   <p className="font-display text-xl uppercase">On Set / Hyderabad</p>
                 </div>
              </div>
            </div>

          </div>
        </section>

        {/* PROGRAM SPECS */}
        <section className="border-b border-neutral-900 bg-neutral-950">
          <div className="grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-neutral-900">
            {[
              { label: "DURATION", value: "8 WEEKS" },
              { label: "FORMAT", value: "LIVE + SPRINT" },
              { label: "COHORT", value: "10 SEATS" },
              { label: "FEE", value: "₹50,000 INR" }
            ].map((item, i) => (
              <div key={i} className="p-8 md:p-12 hover:bg-neutral-900 transition-colors group relative overflow-hidden">
                <span className="font-mono text-xs text-neutral-500 uppercase tracking-widest block mb-4 relative z-10">{item.label}</span>
                <p className="font-display text-4xl md:text-5xl uppercase relative z-10">{item.value}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CURRICULUM - WITH HOVER IMAGE EFFECT */}
{/* CURRICULUM - WITH HOVER IMAGE EFFECT */}
<section className="py-32 px-6 md:px-12 border-b border-neutral-900 relative">
  
  <div className="flex flex-col md:flex-row gap-12 md:gap-32 relative z-10">
    <div className="md:w-1/4 sticky top-32 h-fit">
      <h3 className="font-mono text-xs text-[#E50914] uppercase tracking-widest mb-4">
        The Protocol
      </h3>
      <p className="font-display text-4xl uppercase mb-8">
        From Raw Edit<br/>To Final Render
      </p>
      <div className="h-[2px] w-24 bg-white/10"></div>
    </div>

    <div className="md:w-3/4 space-y-24">
      <CurriculumItem 
        phase="01" 
        title="FOUNDATIONS" 
        desc="Filmmaking fundamentals first. If the cut doesn't work, the AI render won't save it."
        image="/image_0.png" // Path to your new image in the public folder
      />
      <CurriculumItem 
        phase="02" 
        title="CONTROL" 
        desc="Moving beyond slot-machine prompting. Force the AI to adhere to your specific director's vision."
        image="/image_1.png" // Path to your new image in the public folder
      />
      <CurriculumItem 
        phase="03" 
        title="PRODUCTION" 
        desc="Simulating the studio pipeline. Script breakdowns, dailies, and deadline management."
        image="/image_2.png" // Path to your new image in the public folder
      />
    </div>
  </div>
</section>

        {/* HIRING - BIG IMPACT IMAGE */}
 {/* HIRING - STUDIO GRADE DARK MODE */}
<section className="relative py-40 px-6 md:px-12 border-b border-neutral-900 overflow-hidden bg-neutral-950">
  
  {/* BACKGROUND: Deep Red/Black Gradient (Safe fallback if no image) */}
  <div className="absolute inset-0 bg-gradient-to-br from-neutral-950 via-neutral-900 to-[#1a0505]"></div>
  
  {/* OPTIONAL: Uncomment the line below if you add a 'hiring.jpg' to your public folder */}
  {/* <img src="/hiring.jpg" alt="Studio" className="absolute inset-0 w-full h-full object-cover opacity-20 grayscale mix-blend-overlay" /> */}

  <div className="relative z-10 max-w-8xl">
    <h2 className="font-display text-[14vw] md:text-[8vw] leading-[0.85] uppercase mb-8 text-white">
      The Top <span className="text-[#E50914]">1%</span> Gets Hired.
    </h2>
    <p className="max-w-xl text-xl md:text-xl font-light leading-relaxed text-neutral-400 mb-8 border-l-2 border-[#E50914] pl-6">
      We use this program to scout talent for MotionX. In every batch, we aim to identify 1–2 high performers to join our studio on commercial projects.
    </p>
  </div>
</section>

        {/* FILTERING */}
        <section className="py-32 px-6 md:px-12 border-b border-neutral-900 grid md:grid-cols-2 gap-12 bg-neutral-950">
            <div>
              <h3 className="font-display text-4xl uppercase mb-8">Who Makes It</h3>
              <ul className="space-y-4 font-mono text-sm text-neutral-400">
                <li className="flex gap-4 items-center"><Check size={16} className="text-[#E50914]"/> VIDEO EDITORS & DESIGNERS</li>
                <li className="flex gap-4 items-center"><Check size={16} className="text-[#E50914]"/> PEOPLE WHO HANDLE CRITICISM</li>
                <li className="flex gap-4 items-center"><Check size={16} className="text-[#E50914]"/> 10+ HOURS WEEKLY COMMITMENT</li>
              </ul>
            </div>
            <div>
              <h3 className="font-display text-4xl uppercase mb-8 text-neutral-700">Who Doesn't</h3>
              <ul className="space-y-4 font-mono text-sm text-neutral-600">
                <li className="flex gap-4 items-center"><X size={16}/> ABSOLUTE BEGINNERS</li>
                <li className="flex gap-4 items-center"><X size={16}/> PASSIVE INCOME SEEKERS</li>
                <li className="flex gap-4 items-center"><X size={16}/> HOBBYISTS</li>
              </ul>
            </div>
        </section>

        {/* FINAL CTA */}
        <section id="apply" className="relative py-40 px-6 md:px-12 flex flex-col items-center text-center overflow-hidden">
          {/* Subtle Glow Background */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#E50914] blur-[150px] opacity-20 pointer-events-none"></div>

          <p className="font-mono text-xs text-[#E50914] uppercase tracking-widest mb-8 relative z-10">Applications Open for Batch 01</p>
          <h2 className="font-display text-[8vw] md:text-[6vw] uppercase leading-none mb-12 relative z-10">
            Start Your Shift<br/>To Studio Grade.
          </h2>
          <button className="relative z-10 bg-white text-black px-12 py-5 rounded-full font-bold uppercase tracking-widest text-lg hover:bg-[#E50914] hover:text-white transition-colors duration-300" onClick={() => setIsModalOpen(true)}>
            Apply Now
          </button>
        </section>

        {/* FOOTER */}
        <footer className="py-12 px-6 md:px-12 border-t border-neutral-900 flex justify-between items-end bg-neutral-950">
          <div className="font-display text-2xl uppercase text-neutral-800">MotionX</div>
          <div className="text-right font-mono text-xs text-neutral-600 uppercase">
            © 2024 Dream Splice Studios<br/>Hyderabad, India
          </div>
        </footer>

      </main>
    </div>
  );
};

const CurriculumItem = ({ phase, title, desc, image }) => (
  <div className="group grid md:grid-cols-2 gap-8 border-t border-neutral-800 py-12 hover:border-white transition-colors duration-500">
    
    {/* TEXT SIDE */}
    <div className="order-2 md:order-1 flex flex-col justify-between">
      <div>
        <div className="flex items-baseline gap-4 mb-4">
          <h4 className="font-display text-5xl md:text-6xl uppercase text-neutral-800 group-hover:text-white transition-colors duration-500">
            {title}
          </h4>
          <span className="font-mono text-xs text-[#E50914]">0{phase}</span>
        </div>
        <p className="font-mono text-sm text-neutral-500 max-w-md group-hover:text-neutral-300 transition-colors duration-500">
          {desc}
        </p>
      </div>
    </div>
    
    {/* IMAGE SIDE - NOW ROBUST */}
    <div className="order-1 md:order-2 h-64 md:h-80 w-full overflow-hidden rounded-sm bg-neutral-900 relative">
      {/* 1. Use local images (e.g., /curriculum-1.jpg) in your public folder.
          2. The 'bg-neutral-900' above ensures it looks good even if image is missing.
      */}
      <img 
        src={image} 
        alt={title} 
        className="w-full h-full object-cover opacity-60 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700 grayscale group-hover:grayscale-0"
        onError={(e) => { e.target.style.display = 'none'; }} // Hides image if broken so you just see the clean grey box
      />
      
      {/* Hover Overlay Text */}
      <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
         <span className="bg-[#E50914] text-white text-[10px] font-bold px-2 py-1 uppercase tracking-widest">
           Module 0{phase}
         </span>
      </div>
    </div>
  </div>
);

export default LandingPage;